import update from 'immutability-helper';
import {isEmpty} from 'lodash';
import {useMemo} from 'react';

import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  injectDashboardDates,
  useDashboardDates,
} from '../../../utils/dashboardDate';
import {
  AMSEModuleCalibrationHistory,
  AMSEModuleCalibrationHistoryData,
  getAMSEModuleCalibrationHistoryData,
} from './AMSEModuleCalibrationHistory';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export interface AMSEModuleNodeCalibrationHistoryData {
  viewType: 'emodule';
  title: string;
  amsEmoduleCalibration: AMSEModuleCalibrationHistoryData;
}

const getAMSEModuleCalibrationHistory =
  (): AMSEModuleNodeCalibrationHistoryData => ({
    viewType: 'emodule',
    title: 'eModule Calibration History',
    amsEmoduleCalibration: getAMSEModuleCalibrationHistoryData(),
  });

export const AMSEModuleCalibration = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? (value as AMSEModuleNodeCalibrationHistoryData)
        : getAMSEModuleCalibrationHistory(),
    [value]
  );

  const dashboardDates = useDashboardDates(value?.detachedFromDashboardDate);

  const calibrationValue = useMemo(() => {
    const base = config.amsEmoduleCalibration;
    if (!dashboardDates) {
      return base;
    }
    const newItems = injectDashboardDates(base.openedItems, dashboardDates);
    if (newItems === base.openedItems) {
      return base;
    }
    return {...base, openedItems: newItems};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.amsEmoduleCalibration,
    dashboardDates?.date_start,
    dashboardDates?.date_end,
  ]);

  return (
    <AMSEModuleCalibrationHistory
      value={calibrationValue}
      onUpdate={(v) => {
        let detach = false;
        if (
          dashboardDates &&
          v?.openedItems &&
          v.openedItems.length ===
            config.amsEmoduleCalibration.openedItems.length
        ) {
          const activeIdx = v.openedItems.findIndex(
            (it) =>
              it.id === (v.activeId ?? config.amsEmoduleCalibration.activeId)
          );
          const incoming =
            activeIdx >= 0 ? v.openedItems[activeIdx] : undefined;
          if (
            incoming &&
            (incoming.params?.date_start !== dashboardDates.date_start ||
              incoming.params?.date_end !== dashboardDates.date_end)
          ) {
            detach = true;
          }
        }
        const base = update(value ?? getAMSEModuleCalibrationHistory(), {
          amsEmoduleCalibration: {
            $set: v ?? getAMSEModuleCalibrationHistoryData(),
          },
        });
        onUpdate?.(detach ? {...base, detachedFromDashboardDate: true} : base);
      }}
    />
  );
};
